package com.evoapps.evoshift.ui.availabilitylink

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.LinkOff
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.AvailabilityLinkForm
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.GrowthLinks
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.monthInSentence
import com.evoapps.evoshift.ui.user.AvailabilityLegend
import com.evoapps.evoshift.ui.user.CalendarDayCircle
import com.evoapps.evoshift.ui.user.DayPreferenceForm
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.ui.util.dayShort
import com.evoapps.evoshift.ui.util.monthName
import com.evoapps.evoshift.viewmodels.AvailabilityLinkEvent
import com.evoapps.evoshift.viewmodels.AvailabilityLinkUiState
import com.evoapps.evoshift.viewmodels.AvailabilityLinkViewModel
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.availability_link_already_sent
import evoshift.composeapp.generated.resources.availability_link_change
import evoshift.composeapp.generated.resources.availability_link_due
import evoshift.composeapp.generated.resources.availability_link_footer
import evoshift.composeapp.generated.resources.availability_link_greeting
import evoshift.composeapp.generated.resources.availability_link_greeting_no_name
import evoshift.composeapp.generated.resources.availability_link_hint
import evoshift.composeapp.generated.resources.availability_link_invalid_text
import evoshift.composeapp.generated.resources.availability_link_invalid_title
import evoshift.composeapp.generated.resources.availability_link_locked
import evoshift.composeapp.generated.resources.availability_link_made_with
import evoshift.composeapp.generated.resources.availability_link_message
import evoshift.composeapp.generated.resources.availability_link_send
import evoshift.composeapp.generated.resources.availability_link_sent_text
import evoshift.composeapp.generated.resources.availability_link_sent_title
import evoshift.composeapp.generated.resources.availability_link_title
import evoshift.composeapp.generated.resources.availability_requested_days
import evoshift.composeapp.generated.resources.error_state_retry
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import org.jetbrains.compose.resources.stringResource

/** Test tags of the e-mailed link's form (SH-173). */
object AvailabilityLinkTags {
    const val FORM = "availability_link_form"
    const val INVALID = "availability_link_invalid"
    const val SENT = "availability_link_sent"
    const val SEND = "availability_link_send"
    const val MADE_WITH = "availability_link_made_with"
}

/**
 * SH-173: the web page behind an e-mailed availability link (`/d/<token>`): no app chrome, no
 * sign-in. The month on the workplace's slots (the app's calendar day circles and day form),
 * one "Send availability" button, a thank-you with "Change my answer". A link that does not work
 * gets one message whatever the reason.
 */
@Composable
fun AvailabilityLinkScreen(viewModel: AvailabilityLinkViewModel) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    AvailabilityLinkContent(state, viewModel::onEvent)
}

@Composable
fun AvailabilityLinkContent(state: AvailabilityLinkUiState, onEvent: (AvailabilityLinkEvent) -> Unit) {
    Column(Modifier.fillMaxSize().background(Cream)) {
        Header(state.form)
        Box(Modifier.fillMaxWidth().weight(1f)) {
            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(Modifier.widthIn(max = 560.dp).fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    val form = state.form
                    when {
                        state.isLoading && form == null -> Box(Modifier.fillMaxWidth().padding(48.dp), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator(color = DarkGold)
                        }
                        state.invalid -> Invalid()
                        form == null -> LoadFailed(state.loadError?.asString().orEmpty()) { onEvent(AvailabilityLinkEvent.Retry) }
                        state.sent -> Sent(form) { onEvent(AvailabilityLinkEvent.ChangeAnswer) }
                        else -> Form(state, form, onEvent)
                    }
                    Text(
                        stringResource(Res.string.availability_link_footer),
                        style = MaterialTheme.typography.bodySmall,
                        color = DeepNavy.copy(alpha = 0.6f),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
                    )
                    MadeWith()
                }
            }
        }
    }
}

/** SH-239: "Form made with EVoShift · evoshift.app", a link to the site (utm_source=availability_form). */
@Composable
private fun MadeWith() {
    val uriHandler = LocalUriHandler.current
    Text(
        stringResource(Res.string.availability_link_made_with),
        style = MaterialTheme.typography.bodySmall,
        color = DeepNavy.copy(alpha = 0.55f),
        textAlign = TextAlign.Center,
        modifier = Modifier
            .fillMaxWidth()
            .handCursor()
            .clickable { runCatching { uriHandler.openUri(GrowthLinks.madeWith(GrowthLinks.Source.AVAILABILITY_FORM)) } }
            .padding(vertical = 8.dp)
            .testTag(AvailabilityLinkTags.MADE_WITH)
    )
}

@Composable
private fun Header(form: AvailabilityLinkForm?) {
    Column(
        Modifier.fillMaxWidth().background(DeepNavy).padding(horizontal = 16.dp, vertical = 14.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text("EVoShift", color = DarkGold, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelLarge)
        Text(
            stringResource(Res.string.availability_link_title),
            color = Color.White,
            fontWeight = FontWeight.Bold,
            style = MaterialTheme.typography.titleLarge
        )
        form?.let { Text("${monthName(it.monthNumber)} ${it.year}", color = Color.White.copy(alpha = 0.8f), style = MaterialTheme.typography.bodyMedium) }
    }
}

private val AvailabilityLinkForm.year: Int get() = month.take(4).toIntOrNull() ?: 0
private val AvailabilityLinkForm.monthNumber: Int get() = month.drop(5).take(2).toIntOrNull()?.coerceIn(1, 12) ?: 1

/** "20 lutego 2027" of an ISO date or timestamp; empty when unreadable. */
@Composable
private fun fullDate(iso: String?): String {
    val date = iso?.take(10)?.let { runCatching { LocalDate.parse(it) }.getOrNull() } ?: return ""
    return "${dayAndMonth(date)} ${date.year}"
}

@Composable
private fun Invalid() {
    Column(
        Modifier.fillMaxWidth().padding(vertical = 32.dp).testTag(AvailabilityLinkTags.INVALID),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Icon(Icons.Default.LinkOff, contentDescription = null, tint = DeepNavy.copy(alpha = 0.6f), modifier = Modifier.size(48.dp))
        Text(
            stringResource(Res.string.availability_link_invalid_title),
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = DeepNavy,
            textAlign = TextAlign.Center
        )
        Text(
            stringResource(Res.string.availability_link_invalid_text),
            style = MaterialTheme.typography.bodyMedium,
            color = DeepNavy.copy(alpha = 0.8f),
            textAlign = TextAlign.Center
        )
    }
}

@Composable
private fun LoadFailed(message: String, onRetry: () -> Unit) {
    Column(
        Modifier.fillMaxWidth().padding(vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(message, style = MaterialTheme.typography.bodyMedium, color = DeepNavy, textAlign = TextAlign.Center)
        OutlinedButton(onClick = onRetry, modifier = Modifier.handCursor()) {
            Text(stringResource(Res.string.error_state_retry), color = DeepNavy)
        }
    }
}

@Composable
private fun Sent(form: AvailabilityLinkForm, onChange: () -> Unit) {
    Column(
        Modifier.fillMaxWidth().padding(vertical = 32.dp).testTag(AvailabilityLinkTags.SENT),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Icon(Icons.Default.CheckCircle, contentDescription = null, tint = Color(0xFF2E7D32), modifier = Modifier.size(56.dp))
        Text(
            stringResource(Res.string.availability_link_sent_title),
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            color = DeepNavy,
            textAlign = TextAlign.Center
        )
        Text(
            stringResource(Res.string.availability_link_sent_text, fullDate(form.expiresAt)),
            style = MaterialTheme.typography.bodyMedium,
            color = DeepNavy.copy(alpha = 0.8f),
            textAlign = TextAlign.Center
        )
        OutlinedButton(onClick = onChange, modifier = Modifier.handCursor()) {
            Text(stringResource(Res.string.availability_link_change), color = DeepNavy)
        }
    }
}

@Composable
private fun Note(text: String, background: Color) {
    Text(
        text,
        style = MaterialTheme.typography.bodyMedium,
        color = DeepNavy,
        modifier = Modifier.fillMaxWidth().background(background, RoundedCornerShape(12.dp)).padding(12.dp)
    )
}

@Composable
private fun Form(state: AvailabilityLinkUiState, form: AvailabilityLinkForm, onEvent: (AvailabilityLinkEvent) -> Unit) {
    Column(Modifier.fillMaxWidth().testTag(AvailabilityLinkTags.FORM), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        val period = "${monthInSentence(form.monthNumber)} ${form.year}"
        Text(
            if (form.firstName.isNotBlank()) stringResource(Res.string.availability_link_greeting, form.firstName, form.workplaceName, period)
            else stringResource(Res.string.availability_link_greeting_no_name, form.workplaceName, period),
            style = MaterialTheme.typography.bodyLarge,
            color = DeepNavy
        )
        state.requestedDays?.let { days ->
            Text(
                stringResource(Res.string.availability_requested_days, days.first, days.last, fullDate(form.dueDate)),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = DeepNavy
            )
        } ?: fullDate(form.dueDate).takeIf { it.isNotEmpty() }?.let {
            Text(stringResource(Res.string.availability_link_due, it), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
        }
        form.message?.let { Note(stringResource(Res.string.availability_link_message, it), Color(0xFFF7F2E9)) }
        when {
            form.locked -> Note(stringResource(Res.string.availability_link_locked), Color(0xFFFDECEA))
            form.submitted -> Note(stringResource(Res.string.availability_link_already_sent), com.evoapps.evoshift.ui.AppAppearance.palette.base.noteTint)
        }
        if (!form.locked) {
            Text(stringResource(Res.string.availability_link_hint), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f))
        }
        AvailabilityLegend(showOutside = state.requestedDays != null)
        MonthGrid(state, form, onEvent)

        val selected = state.selectedDay
        if (selected != null) {
            val date = LocalDate(form.year, form.monthNumber, selected)
            DayPreferenceForm(
                date = date,
                preference = state.days[selected] ?: DayPreference(AvailabilityStatus.AVAILABLE, null, null),
                availableSlots = form.slots,
                isReadOnly = state.isReadOnly,
                asPanel = true,
                embedded = true,
                onHoursChanged = { onEvent(AvailabilityLinkEvent.SetHours(selected, it)) },
                onPreferenceChanged = { onEvent(AvailabilityLinkEvent.UpdateDay(selected, it)) }
            )
        }

        state.error?.let { Note(it.asString(), Color(0xFFFDECEA)) }
        if (!form.locked) {
            Button(
                onClick = { onEvent(AvailabilityLinkEvent.Submit) },
                enabled = !state.isSending,
                colors = ButtonDefaults.buttonColors(containerColor = DeepNavy, contentColor = Color.White),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp).handCursor().testTag(AvailabilityLinkTags.SEND)
            ) {
                if (state.isSending) {
                    CircularProgressIndicator(color = Color.White, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
                } else {
                    Icon(Icons.AutoMirrored.Filled.Send, contentDescription = null, modifier = Modifier.size(18.dp))
                }
                Spacer(Modifier.size(8.dp))
                Text(stringResource(Res.string.availability_link_send), fontWeight = FontWeight.Bold)
            }
        }
    }
}

/** The month as weeks of the app's day circles, starting on the workplace's first day of the week. */
@Composable
private fun MonthGrid(state: AvailabilityLinkUiState, form: AvailabilityLinkForm, onEvent: (AvailabilityLinkEvent) -> Unit) {
    val days = state.days.keys.sorted()
    if (days.isEmpty()) return
    val first = LocalDate(form.year, form.monthNumber, 1)
    val offset = (first.dayOfWeek.isoDayNumber - form.firstDayOfWeek + 7) % 7
    val cells: List<Int?> = List(offset) { null } + days
    val order = (0..6).map { DayOfWeek((it + form.firstDayOfWeek - 1) % 7 + 1) }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
            order.forEach { day ->
                Text(
                    dayShort(day),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.Gray,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.weight(1f)
                )
            }
        }
        cells.chunked(7).forEach { week ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                (0 until 7).forEach { i ->
                    Box(Modifier.weight(1f), contentAlignment = Alignment.Center) {
                        val day = week.getOrNull(i)
                        if (day == null) {
                            Spacer(Modifier.height(48.dp))
                        } else {
                            CalendarDayCircle(
                                day = day,
                                status = state.days[day]?.status ?: AvailabilityStatus.AVAILABLE,
                                isSelected = state.selectedDay == day,
                                outsideRequest = !state.isRequested(day),
                                onClick = { onEvent(AvailabilityLinkEvent.SelectDay(day)) }
                            )
                        }
                    }
                }
            }
        }
    }
}
